// Client-side routing — port of the Python route_server.compute_route pipeline.
// Input: graph.json (nodes + edges), startId, endId, pt_to_m.
// Output: same route shape the Python server returns:
//   { from, to, total_length_pt, total_length_m_approx, spur_edges,
//     bridge_edges, points_pt, steps }

const SPUR_COST_PENALTY = 100.0;
const SIMPLIFY_TOL_PT = 15.0;
const ORTHO_SNAP_DEG = 12.0;
const TURN_THRESHOLD_DEG = 50;

// --- Graph construction ---

export function buildGraph(graphJson) {
  const nodes = new Map();
  const adj = new Map();               // nodeId → [{to, edge}]
  const edges = [];                    // flat list for quick lookup
  for (const n of graphJson.nodes) {
    nodes.set(n.id, n);
    adj.set(n.id, []);
  }
  for (const e of graphJson.edges) {
    const cost = e.length_pt * (e.via_spur ? SPUR_COST_PENALTY : 1);
    const rec = { ...e, cost };
    edges.push(rec);
    adj.get(e.u).push({ to: e.v, edge: rec });
    adj.get(e.v).push({ to: e.u, edge: rec });
  }
  // Index lobbies + pillars by label for fast lookup.
  const lobbies = new Map(), pillars = new Map();
  for (const n of graphJson.nodes) {
    if (n.type === "lobby" && n.label) lobbies.set(n.label, n.id);
    else if (n.type === "pillar" && n.label) pillars.set(n.label, n.id);
  }
  return { nodes, adj, edges, lobbies, pillars, zoom: graphJson.zoom };
}

// --- Dijkstra shortest path ---

function shortestPath(graph, startId, endId) {
  const { adj } = graph;
  const dist = new Map(), prev = new Map();
  for (const id of adj.keys()) dist.set(id, Infinity);
  dist.set(startId, 0);
  // Simple binary heap — adequate for our ~500-node graphs.
  const heap = [[0, startId]];
  const heapPush = (item) => {
    heap.push(item);
    let i = heap.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (heap[p][0] <= heap[i][0]) break;
      [heap[p], heap[i]] = [heap[i], heap[p]]; i = p;
    }
  };
  const heapPop = () => {
    const top = heap[0], last = heap.pop();
    if (heap.length) {
      heap[0] = last; let i = 0;
      for (;;) {
        const l = 2 * i + 1, r = l + 1;
        let s = i;
        if (l < heap.length && heap[l][0] < heap[s][0]) s = l;
        if (r < heap.length && heap[r][0] < heap[s][0]) s = r;
        if (s === i) break;
        [heap[s], heap[i]] = [heap[i], heap[s]]; i = s;
      }
    }
    return top;
  };
  while (heap.length) {
    const [d, u] = heapPop();
    if (u === endId) break;
    if (d > dist.get(u)) continue;
    for (const { to, edge } of adj.get(u)) {
      const nd = d + edge.cost;
      if (nd < dist.get(to)) {
        dist.set(to, nd);
        prev.set(to, u);
        heapPush([nd, to]);
      }
    }
  }
  if (!prev.has(endId) && startId !== endId) return null;
  const path = [endId];
  let cur = endId;
  while (cur !== startId) {
    cur = prev.get(cur);
    if (cur == null) return null;
    path.push(cur);
  }
  path.reverse();
  return path;
}

// --- Stitch polyline points along the chosen path ---

function stitchPoints(graph, path) {
  const { nodes, adj } = graph;
  const pts = [];
  for (let i = 0; i < path.length - 1; i++) {
    const u = path[i], v = path[i + 1];
    // Find the edge record for this pair.
    const rec = adj.get(u).find(a => a.to === v);
    if (!rec) continue;
    let seg = rec.edge.points_px.map(p => [p[0], p[1]]);
    const uNode = nodes.get(u);
    if (!(Math.abs(seg[0][0] - uNode.x_px) < 0.5 && Math.abs(seg[0][1] - uNode.y_px) < 0.5)) {
      seg.reverse();
    }
    if (pts.length && pts[pts.length - 1][0] === seg[0][0]
        && pts[pts.length - 1][1] === seg[0][1]) {
      pts.push(...seg.slice(1));
    } else {
      pts.push(...seg);
    }
  }
  return pts;
}

// --- Douglas-Peucker polyline simplify ---
// Keeps only points whose perpendicular distance from the chord exceeds tol.

function simplify(points, tol) {
  if (points.length < 3) return points.slice();
  const tol2 = tol * tol;
  const keep = new Uint8Array(points.length);
  keep[0] = 1; keep[points.length - 1] = 1;
  const stack = [[0, points.length - 1]];
  while (stack.length) {
    const [lo, hi] = stack.pop();
    if (hi <= lo + 1) continue;
    let maxD2 = 0, maxI = -1;
    const [x1, y1] = points[lo], [x2, y2] = points[hi];
    const dx = x2 - x1, dy = y2 - y1;
    const segLen2 = dx * dx + dy * dy;
    for (let i = lo + 1; i < hi; i++) {
      const [px, py] = points[i];
      let d2;
      if (segLen2 === 0) {
        const ex = px - x1, ey = py - y1;
        d2 = ex * ex + ey * ey;
      } else {
        const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / segLen2));
        const cx = x1 + t * dx, cy = y1 + t * dy;
        const ex = px - cx, ey = py - cy;
        d2 = ex * ex + ey * ey;
      }
      if (d2 > maxD2) { maxD2 = d2; maxI = i; }
    }
    if (maxD2 > tol2 && maxI >= 0) {
      keep[maxI] = 1;
      stack.push([lo, maxI]);
      stack.push([maxI, hi]);
    }
  }
  const out = [];
  for (let i = 0; i < points.length; i++) if (keep[i]) out.push(points[i]);
  return out;
}

// --- Orthogonal snap + diagonal→L ---
// Mirrors Python `_snap_ortho`: first snap near-axis segments to axis, then
// replace remaining diagonals with horizontal-then-vertical L pairs.

function snapOrtho(points) {
  if (points.length < 2) return points.slice();
  // Pass 1: near-axis snap
  const out = [points[0]];
  for (let i = 1; i < points.length; i++) {
    let [x1, y1] = points[i];
    const [x0, y0] = out[out.length - 1];
    const dx = x1 - x0, dy = y1 - y0;
    if (dx === 0 && dy === 0) { out.push([x1, y1]); continue; }
    const bearing = ((Math.atan2(dy, dx) * 180 / Math.PI) + 360) % 360;
    for (const axis of [0, 90, 180, 270]) {
      const diff = Math.abs(((bearing - axis + 180) % 360) - 180);
      if (diff <= ORTHO_SNAP_DEG) {
        if (axis === 0 || axis === 180) { y1 = y0; }  // horizontal
        else { x1 = x0; }                              // vertical
        break;
      }
    }
    out.push([x1, y1]);
  }
  // Pass 2: diagonal → L
  const lpts = [out[0]];
  for (let i = 1; i < out.length; i++) {
    const [x1, y1] = out[i];
    const [x0, y0] = lpts[lpts.length - 1];
    const dx = x1 - x0, dy = y1 - y0;
    if (dx === 0 || dy === 0) { lpts.push([x1, y1]); continue; }
    lpts.push([x1, y0]);
    lpts.push([x1, y1]);
  }
  return lpts;
}

// --- Step generation (turn-by-turn) ---

function bearingDeg(p1, p2) {
  return Math.atan2(p2[1] - p1[1], p2[0] - p1[0]) * 180 / Math.PI;
}

function describeTurn(prevBearing, nextBearing) {
  let delta = ((nextBearing - prevBearing + 540) % 360) - 180;
  const abs = Math.abs(delta);
  if (abs < TURN_THRESHOLD_DEG) return "continue straight";
  if (abs > 160) return "turn around";
  const dir = delta > 0 ? "right" : "left";
  if (abs < 70) return `turn slightly ${dir}`;
  if (abs < 120) return `turn ${dir}`;
  return `turn sharply ${dir}`;
}

function generateSteps(simplifiedPts, startLabel, endLabel, ptToM) {
  // Prospective step semantics: step[N].position = user's current location,
  // step[N].text = action FROM that position (walk to next waypoint + turn
  // awaiting there). Matches the reading direction of Google-Maps-style
  // instructions — cursor sits at the start of the described walk.
  const toM = (v) => Math.max(1, Math.round(v * ptToM));
  const r2 = (v) => Math.round(v * 100) / 100;
  if (simplifiedPts.length < 2) {
    return [{ text: `You're already at ${endLabel}.`,
              x: r2(simplifiedPts[0][0]), y: r2(simplifiedPts[0][1]) }];
  }

  // 1) Identify waypoint vertex indices: start + each turn + destination.
  const waypointIdx = [0];
  let prev = null;
  for (let i = 0; i < simplifiedPts.length - 1; i++) {
    const nb = bearingDeg(simplifiedPts[i], simplifiedPts[i + 1]);
    if (prev == null) { prev = nb; continue; }
    if (describeTurn(prev, nb) !== "continue straight") waypointIdx.push(i);
    prev = nb;
  }
  const lastIdx = simplifiedPts.length - 1;
  if (waypointIdx[waypointIdx.length - 1] !== lastIdx) waypointIdx.push(lastIdx);

  const walkDistance = (aIdx, bIdx) => {
    let d = 0;
    for (let j = aIdx; j < bIdx; j++) {
      d += Math.hypot(simplifiedPts[j + 1][0] - simplifiedPts[j][0],
                      simplifiedPts[j + 1][1] - simplifiedPts[j][1]);
    }
    return d;
  };

  const segBearing = (k) => {
    if (k >= waypointIdx.length - 1) return null;
    const a = simplifiedPts[waypointIdx[k]];
    const b = simplifiedPts[waypointIdx[k + 1]];
    return bearingDeg(a, b);
  };

  // 2) Emit one step per waypoint with prospective text.
  const steps = [];
  for (let k = 0; k < waypointIdx.length; k++) {
    const p = simplifiedPts[waypointIdx[k]];
    const entry = { x: r2(p[0]), y: r2(p[1]) };

    if (k === waypointIdx.length - 1) {
      entry.text = `Arrived at ${endLabel}.`;
      steps.push(entry);
      continue;
    }

    const dist = walkDistance(waypointIdx[k], waypointIdx[k + 1]);
    const nextIsDest = (k + 1 === waypointIdx.length - 1);

    let walkText;
    if (nextIsDest) {
      walkText = `Walk ~${toM(dist)} m — ${endLabel} is on your side.`;
    } else {
      const pb = segBearing(k), nb = segBearing(k + 1);
      const turn = (pb != null && nb != null) ? describeTurn(pb, nb) : "";
      walkText = `Walk ~${toM(dist)} m, then ${turn}.`;
    }

    entry.text = k === 0 ? `Start at ${startLabel}. ${walkText}` : walkText;
    entry.distance_pt = r2(dist);
    entry.distance_m_approx = toM(dist);
    steps.push(entry);
  }
  return steps;
}

// --- Public entrypoint ---

export function computeRoute(graph, startLabel, endLabel, ptToM) {
  const startId = graph.lobbies.get(startLabel)
                 ?? graph.lobbies.get(startLabel.toUpperCase());
  const endId   = graph.pillars.get(endLabel)
                 ?? graph.pillars.get(endLabel.toUpperCase());
  if (startId == null) throw new Error(`Unknown lobby: ${startLabel}`);
  if (endId == null)   throw new Error(`Unknown pillar: ${endLabel}`);

  const path = shortestPath(graph, startId, endId);
  if (!path) throw new Error(`No path from ${startLabel} to ${endLabel}`);

  const ptsPx = stitchPoints(graph, path);
  const ptsPt = ptsPx.map(([x, y]) => [x / graph.zoom, y / graph.zoom]);
  let simp = ptsPt.length >= 3 ? simplify(ptsPt, SIMPLIFY_TOL_PT) : ptsPt.slice();
  simp = snapOrtho(simp).map(([x, y]) => [
    Math.round(x * 100) / 100,
    Math.round(y * 100) / 100,
  ]);

  let totalPt = 0;
  for (let i = 0; i < simp.length - 1; i++) {
    totalPt += Math.hypot(simp[i + 1][0] - simp[i][0], simp[i + 1][1] - simp[i][1]);
  }

  const startNode = graph.nodes.get(startId);
  const endNode   = graph.nodes.get(endId);
  const steps = generateSteps(simp, startLabel, endLabel, ptToM);

  let spurCount = 0, bridgeCount = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const u = path[i], v = path[i + 1];
    const rec = graph.adj.get(u).find(a => a.to === v);
    if (rec) {
      if (rec.edge.via_spur)   spurCount++;
      if (rec.edge.via_bridge) bridgeCount++;
    }
  }

  return {
    from: { id: startId, label: startLabel,
      x_pt: Math.round(startNode.x_pt * 100) / 100,
      y_pt: Math.round(startNode.y_pt * 100) / 100 },
    to:   { id: endId, label: endLabel,
      x_pt: Math.round(endNode.x_pt * 100) / 100,
      y_pt: Math.round(endNode.y_pt * 100) / 100 },
    total_length_pt: Math.round(totalPt * 100) / 100,
    total_length_m_approx: Math.round(totalPt * ptToM * 10) / 10,
    spur_edges: spurCount,
    bridge_edges: bridgeCount,
    points_pt: simp,
    steps,
  };
}
